import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { TDashboard, TWidget } from 'types/hubDashboard.type';
import type { IDefaultReducerState } from 'types/state.interface';
import { errorNotification } from 'utils/notifications';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { validateHubDashboard } from 'utils/validations/hubDashboard.validation';
import api from '../services/api.service';

export type THubDashboardState = IDefaultReducerState & {
  data: TDashboard[];
  selectedDashboard: TDashboard | null;
};

export const initialState: THubDashboardState = {
  loading: false,
  hasErrors: false,
  selectedDashboard: null,
  data: [],
};

const createEmptyDashboard = (): TDashboard => ({
  id: 'new',
  name: '',
  roles: [],
  widgets: [],
});

// Slice
const slice = createSlice({
  name: 'hubDashboard',
  initialState,
  reducers: {
    fetchHubDashboardRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchHubDashboardSuccess: (
      state,
      { payload }: PayloadAction<TDashboard[]>
    ) => {
      state.data = payload;
      finishedLoadingSuccess(state);
    },
    fetchHubDashboardFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createHubDashboardRequest: (state) => {
      isLoadingRequest(state);
    },
    createHubDashboardSuccess: (
      state,
      { payload }: PayloadAction<TDashboard>
    ) => {
      const findIndex = state.data.findIndex((item) => item.id === 'new');
      if (findIndex !== -1) {
        state.data[findIndex] = payload;
      }
      state.selectedDashboard = payload;
      finishedLoadingSuccess(state);
    },
    createHubDashboardFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateHubDashboardRequest: (state) => {
      isLoadingRequest(state);
    },
    updateHubDashboardSuccess: (
      state,
      { payload }: PayloadAction<TDashboard>
    ) => {
      const findIndex = state.data.findIndex((item) => item.id === payload.id);
      if (findIndex !== -1) {
        state.data[findIndex] = payload;
      }
      finishedLoadingSuccess(state);
    },
    updateHubDashboardFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteHubDashboardRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteHubDashboardSuccess: (state, { payload }: PayloadAction<string>) => {
      state.data = state.data.filter((item) => item.id !== payload);
      if (state.selectedDashboard?.id === payload) {
        state.selectedDashboard = state.data?.[0] || null; // Select the first dashboard if available
      }
      finishedLoadingSuccess(state);
    },
    deleteHubDashboardFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createUnsaveDashboard: (state) => {
      if (state.data.some((item) => item.id === 'new')) {
        return; // Prevent creating multiple unsaved dashboards
      }
      const newDashboard = createEmptyDashboard();
      state.data.unshift(newDashboard);
      state.selectedDashboard = newDashboard;
    },
    deleteUnsaveDashboard: (state) => {
      state.data = state.data.filter((item) => item.id !== 'new');
      if (state.selectedDashboard?.id === 'new') {
        state.selectedDashboard = state.data?.[0] || null; // Select the first dashboard if available
      }
    },
    setSelectedDashboard: (state, { payload }: PayloadAction<string>) => {
      const foundDashboard = state.data.find(
        (dashboard) => dashboard.id === payload
      );
      state.selectedDashboard = foundDashboard;
    },
    updatedSelectedDashboard: (
      state,
      { payload }: PayloadAction<Partial<TDashboard>>
    ) => {
      if (!state.selectedDashboard) return;

      state.selectedDashboard = {
        ...state.selectedDashboard,
        ...payload,
      };

      const findIndex = state.data.findIndex(
        (item) => item.id === state.selectedDashboard?.id
      );
      if (findIndex !== -1) {
        state.data[findIndex] = state.selectedDashboard;
      }
    },
    updateWidgetOrder: (
      state,
      { payload }: PayloadAction<{ activeIndex: number; overIndex: number }>
    ) => {
      if (!state.selectedDashboard) return;

      const { activeIndex, overIndex } = payload;
      const widgets = state.selectedDashboard.widgets;

      // Reorder the widgets array
      const [movedWidget] = widgets.splice(activeIndex, 1);
      widgets.splice(overIndex, 0, movedWidget);

      state.selectedDashboard = {
        ...state.selectedDashboard,
        widgets,
      };
    },
    removeWidget: (state, { payload }: PayloadAction<number>) => {
      if (!state.selectedDashboard) return;

      state.selectedDashboard.widgets.splice(payload, 1);
    },
    addWidget: (state, { payload }: PayloadAction<TWidget>) => {
      if (!state.selectedDashboard) return;

      state.selectedDashboard.widgets.push(payload);
    },
    updateWidget: (
      state,
      { payload }: PayloadAction<{ index: number; data: Partial<TWidget> }>
    ) => {
      if (!state.selectedDashboard) return;

      const { index, data } = payload;
      const widget = state.selectedDashboard.widgets[index];
      if (widget) {
        state.selectedDashboard.widgets[index] = {
          ...state.selectedDashboard.widgets[index],
          ...data,
        };
      }
    },
  },
});

export default slice.reducer;

// Selectors
export const hubDashboardSelector = (state: TRootState): TDashboard[] =>
  state.hubDashboard.data;

export const hubDashboardLoadingSelector = (state: TRootState): boolean =>
  state.hubDashboard.loading;

export const selectedHubDashboardSelector = (
  state: TRootState
): TDashboard | null => state.hubDashboard.selectedDashboard;

// Actions
export const {
  fetchHubDashboardRequest,
  fetchHubDashboardSuccess,
  fetchHubDashboardFailure,
  createHubDashboardRequest,
  createHubDashboardSuccess,
  createHubDashboardFailure,
  updateHubDashboardRequest,
  updateHubDashboardSuccess,
  updateHubDashboardFailure,
  deleteHubDashboardRequest,
  deleteHubDashboardSuccess,
  deleteHubDashboardFailure,
  createUnsaveDashboard,
  deleteUnsaveDashboard,
  setSelectedDashboard,
  updatedSelectedDashboard,
  updateWidgetOrder,
  removeWidget,
  updateWidget,
  addWidget,
} = slice.actions;

export const fetchHubDashboard = createAppAsyncThunk(
  'hubDashboard/fetchHubDashboard',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchHubDashboardRequest());
      const result = await api.get({
        endpoint: `/hub/dashboards`,
      });
      dispatch(fetchHubDashboardSuccess(result));
      // initially select the first dashboard if available
      dispatch(setSelectedDashboard(result?.[0]?.id || ''));
    } catch (e) {
      dispatch(fetchHubDashboardFailure());
      console.error(e);
    }
  }
);

export const createHubDashboard = createAppAsyncThunk(
  'hubDashboard/createHubDashboard',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(createHubDashboardRequest());
      const state = getState();
      const selectedDashboard = state.hubDashboard?.selectedDashboard;

      if (!selectedDashboard) {
        throw new Error('No dashboard is selected for update.');
      }
      const { id, ...payload } = selectedDashboard;

      const validated = validateHubDashboard(payload);

      if (!validated.success) {
        errorNotification(
          'Configuration could not be validated',
          validated.error.issues.map((error) => error.message)
        );
        throw new Error('Configuration could not be validated');
      }

      const result = await api.post({
        endpoint: `/hub/dashboards`,
        data: payload,
      });
      dispatch(createHubDashboardSuccess(result));
    } catch (e) {
      dispatch(createHubDashboardFailure());
      console.error(e);
    }
  }
);

export const updateHubDashboard = createAppAsyncThunk(
  'hubDashboard/updateHubDashboard',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(updateHubDashboardRequest());
      const state = getState();
      const selectedDashboard = state.hubDashboard?.selectedDashboard;
      if (!selectedDashboard) {
        throw new Error('No dashboard is selected for update.');
      }
      const { id, ...payload } = selectedDashboard;

      const validated = validateHubDashboard(payload);

      if (!validated.success) {
        errorNotification(
          'Configuration could not be validated',
          validated.error.issues.map((error) => error.message)
        );
        throw new Error('Configuration could not be validated');
      }

      const result = await api.put({
        endpoint: `/hub/dashboards/${id}`,
        data: payload,
      });

      dispatch(updateHubDashboardSuccess(result));
    } catch (e) {
      dispatch(updateHubDashboardFailure());
      console.error(e);
    }
  }
);

export const deleteHubDashboard = createAppAsyncThunk(
  'hubDashboard/deleteHubDashboard',
  async (dashboardId: string, { dispatch }) => {
    try {
      dispatch(deleteHubDashboardRequest());
      await api.delete({
        endpoint: `/hub/dashboards/${dashboardId}`,
      });
      dispatch(deleteHubDashboardSuccess(dashboardId));
    } catch (e) {
      dispatch(deleteHubDashboardFailure());
      console.error(e);
    }
  }
);
